// KnowledgeCarousel 中点击知识库项也打开视频弹窗
import React, { useMemo, useState, useRef } from "react";
import {
  ChevronRightThinIcon,
  ChevronLeftThinIcon,
} from "@/components/ui/icon";
import { Image } from "@/components/common/image";
import { TruncatedText } from "@/components/common/truncated-text";
import knowledgeBg from '@/assets/assets/knowledge-bg.webp';
import { useTranslation } from "react-i18next";
import { KnowledgeEmptyIcon } from "../agent-dashboard-user-detail/assets";
import { KnowledgeModalDetail } from "../knowledge-model-detail";
import { ItemDetailData, knowledgeGetFolderItems } from "@/services/knowledge-get-folder-items";

type KnowledgeItem = {
  id: number;
  folderId: number;
  folderName: string;
};

export const KnowledgeCarousel: React.FC<{
  knowledge?: KnowledgeItem[];
  onOpenAll?: (items: KnowledgeItem[]) => void;
  loading?: boolean;
}> = ({ knowledge, onOpenAll, loading }) => {
  const { t } = useTranslation();
  const items = useMemo(() => knowledge || [], [knowledge]);
  const pageSize = 8; // 4列 × 2行
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const [page, setPage] = useState(0);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<{ id: number; name: string } | null>(null);
  const [folderItems, setFolderItems] = useState<ItemDetailData[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);

  const handleFolderClick = async (id: number, name: string) => {
    setSelectedFolder({ id, name });
    setDetailOpen(true);
    setItemsLoading(true);
    setFolderItems([]); // Clear previous items
    try {
      const res = await knowledgeGetFolderItems(id);
      setFolderItems(res.items.map(item => item.itemDetail[0]) || []);
    } catch (e) {
      console.error(e);
    } finally {
      setItemsLoading(false);
    }
  };

  const startXRef = useRef<number | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    startXRef.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (startXRef.current == null) return;
    const diff = e.changedTouches[0].clientX - startXRef.current;
    if (Math.abs(diff) > 50) {
      if (diff < 0) setPage((p) => Math.min(totalPages - 1, p + 1));
      else setPage((p) => Math.max(0, p - 1));
    }
    startXRef.current = null;
  };
  const handleWheel = (e: React.WheelEvent) => {
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      if (e.deltaX > 0) setPage((p) => Math.min(totalPages - 1, p + 1));
      else setPage((p) => Math.max(0, p - 1));
    }
  };

  const canScrollLeft = page > 0;
  const canScrollRight = page < totalPages - 1;
  const pageItems = items.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <div className="w-full h-full flex flex-col">
      {/* 头部 */}
      <div className="flex items-center justify-between mb-5">
        <div className="text-[15px] text-[rgba(16,24,40,1)] font-medium">
          {t("agent_creation.subscription_settings.knowledge")}
        </div>
        <button
          className="flex items-center gap-2"
          onClick={() => onOpenAll(items)}
        >
          <span className="text-sm text-[rgba(102,112,133,1)]">
            {t("agent_creation.subscription_settings.total_items", { totalItems: items.length })}
          </span>
          <ChevronLeftThinIcon className="w-5 h-5 text-[rgba(152,162,179,1)]" />
        </button>
      </div>

      <div
        className="relative select-none"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onWheel={handleWheel}
      >
        {/* 左侧渐变遮罩 + 按钮 */}
        {canScrollLeft && (
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="absolute -left-5 top-1/2 -translate-y-1/2 h-full w-12 flex items-center justify-center z-10 bg-[linear-gradient(270deg,rgba(255,255,255,0)_0%,rgba(255,255,255,1)_80%)]"
          >
            <div className="flex justify-center items-center -mt-4 w-6 h-6 bg-white rounded-full shadow-[0px_1.2px_2.4px_rgba(16,_24,_40,_0.05)]">
              <ChevronRightThinIcon className="w-4 h-4 text" />
            </div>
          </button>
        )}

        {/* 4x2 网格分页 */}
        <div
          className="grid gap-x-4 gap-y-4"
          style={{ gridTemplateColumns: "repeat(4, 1fr)" }}
        >
          {loading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="w-full">
                <div className="w-full aspect-[1] rounded-[8px] bg-gray-200 animate-pulse" />
                <div className="h-4 bg-gray-200 rounded mx-auto w-2/3 mt-2 animate-pulse" />
              </div>
            ))
          ) : (
            pageItems?.length ? pageItems.map((m) => (
              <button
                key={m.id}
                className="w-full text-left"
                onClick={() => handleFolderClick(m.folderId, m.folderName)}
              >
                <div className="w-full aspect-[1] overflow-hidden flex items-center justify-center">
                  <Image
                    src={knowledgeBg}
                    className="w-full h-auto object-contain"
                    alt={m.folderName}
                  />
                </div>
                <TruncatedText className="max-w-[90px] text-center text-sm text-[rgba(16,24,40,1)] font-normal" text={m.folderName} />
              </button>
            )) : <div className='w-full h-full relative  flex flex-col justify-center items-center col-span-full mt-3 mb-16'>
              <KnowledgeEmptyIcon className='w-[159px] h-[136px]' />
              <span className='text-base text-[rgba(102,112,133,1)]'>{t("agent_dashboard.user_detail.knowledge.detail_modal.empty_tips2")}</span>
            </div>
          )}
        </div>
        {/* 右侧渐变与按钮 */}
        {canScrollRight && (
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            className="absolute -right-5 top-1/2 -translate-y-1/2 h-full w-12 flex items-center justify-center z-10 bg-[linear-gradient(90deg,rgba(255,255,255,0)_0%,rgba(255,255,255,1)_80%)]"
          >
            <div className="flex justify-center items-center -mt-4 w-6 h-6 bg-white rounded-full shadow-[0px_1.2px_2.4px_rgba(16,_24,_40,_0.05)]">
              <ChevronLeftThinIcon className="w-4 h-4 text-i-sub" />
            </div>
          </button>
        )}

        {/* 页码指示 */}
        <div className="mt-3 flex items-center justify-center gap-1">
          {totalPages > 1 &&
            Array.from({ length: totalPages }).map((_, i) => (
              <div
                key={i}
                className={`h-1.5 w-1.5 rounded-full ${i === page ? "bg-[#111827]" : "bg-[rgba(213,215,218,1)]"
                  }`}
              />
            ))}
        </div>
      </div>
      <KnowledgeModalDetail
        open={detailOpen}
        onOpenChange={setDetailOpen}
        name={selectedFolder?.name}
        knowledgeFolderItems={folderItems}
        loading={itemsLoading}
      />
    </div>
  );
};
